import React, { useState, useEffect } from 'react';

const scoreColor = s => s >= 4?'#2E7D32':s >= 3?'#1565C0':s >= 2?'#F57F17':'#C62828';

const statusPill = s => {
  const map = {provided:'pill-green',partial:'pill-amber',missing:'pill-red',pending:'pill-gray'};
  const labels = {provided:'Compliant',partial:'Partial',missing:'Gap',pending:'Pending'};
  return <span className={`pill ${map[s]||'pill-gray'}`}>{labels[s]||s}</span>;
};

const sourceChip = s => {
  if (s==='Manual') return <span className="chip chip-manual">Manual</span>;
  if (s==='Upload') return <span className="chip chip-upload">Upload</span>;
  if (s.includes('KQL')||s==='Sentinel') return <span className="chip chip-kql">{s}</span>;
  return <span className="chip chip-ms">{s}</span>;
};

// Determine which layer scored this control
function scoredVia(c) {
  if (c.auto_scored) return { label:'Auto-scored', color:'#2E7D32', bg:'#E8F5E9', page:'auto' };
  if (c.source === 'Upload') return { label:'Document upload', color:'#6A1B9A', bg:'#F3E5F5', page:'uploads' };
  return { label:'Manual / Interview', color:'#455A64', bg:'#F1F3F5', page:'manual' };
}

const ProvenanceBadge = ({ ctrl }) => {
  const src = ctrl.score_source;
  const confirmed = ctrl.score_confirmed;
  if (!ctrl.score) return null;
  if (src === 'assessor_override') return (
    <span title={`Override: ${ctrl.override_reason || '—'}`} style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#FFF3E0',color:'#E65100',fontWeight:500,cursor:'help'}}>Overridden</span>
  );
  if (src === 'auto_confirmed' || (src === 'auto' && confirmed)) return (
    <span title={`Confirmed by ${ctrl.confirmed_by} at ${ctrl.confirmed_at?.split('T')[0]}`} style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#E8F5E9',color:'#2E7D32',fontWeight:500,cursor:'help'}}>Confirmed</span>
  );
  if (src === 'assessor_input') return (
    <span style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#F3E5F5',color:'#6A1B9A',fontWeight:500}}>Assessor</span>
  );
  if (src === 'document_ai') return (
    <span style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#F3E5F5',color:'#6A1B9A',fontWeight:500}}>Document AI</span>
  );
  if (ctrl.auto_scored && !confirmed) return (
    <span style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:'#FFF8E1',color:'#F57F17',fontWeight:500}}>Needs review</span>
  );
  return null;
};

export default function EvidenceCollection({ API, domains, engagement, setPage }) {
  const [selectedDomain, setSelectedDomain] = useState('D1');
  const [controls, setControls] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLayer, setFilterLayer] = useState('all');

  const assessorName = engagement?.assessor || 'Assessor';

  const loadControls = () => {
    fetch(`${API}/api/controls?domain=${selectedDomain}`)
      .then(r => r.json()).then(setControls);
  };

  useEffect(() => { loadControls(); }, [selectedDomain, API]);

  // Counts
  const allControls = controls;
  const scored = allControls.filter(c => c.score != null);
  const confirmed = allControls.filter(c => c.score_confirmed);
  const gaps = allControls.filter(c => c.status === 'missing');
  const pending = allControls.filter(c => c.status === 'pending');
  const autoUnconfirmed = allControls.filter(c => c.auto_scored && !c.score_confirmed && c.score);

  // Filtering
  let filtered = allControls;
  if (filterStatus !== 'all') filtered = filtered.filter(c => c.status === filterStatus);
  if (filterLayer === 'auto') filtered = filtered.filter(c => c.auto_scored);
  if (filterLayer === 'upload') filtered = filtered.filter(c => c.source === 'Upload');
  if (filterLayer === 'manual') filtered = filtered.filter(c => !c.auto_scored && c.source !== 'Upload');

  return (
    <div>

      {/* ── Summary stats ── */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
        {[
          { val:scored.length, total:allControls.length, label:'Scored', color:'#1565C0' },
          { val:confirmed.length, total:scored.length, label:'Confirmed', color:'#2E7D32' },
          { val:gaps.length, total:allControls.length, label:'Gaps', color:'#C62828' },
          { val:pending.length, total:allControls.length, label:'Pending', color:'#F57F17' },
          { val:autoUnconfirmed.length, total:null, label:'Need review', color:'#E65100' },
        ].map((s,i) => (
          <div key={i} className="stat-card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:22,fontWeight:700,color:s.color,lineHeight:1}}>{s.val}{s.total != null ? <span style={{fontSize:12,fontWeight:400,color:'#B0BAC5'}}>/{s.total}</span> : null}</div>
            <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Guidance banner ── */}
      <div className="info-box info-box-blue" style={{marginBottom:16}}>
        <strong>Evidence Collection</strong> is your master review dashboard. Each control shows where it was scored (Auto-scored, Document Upload, or Manual/Interview) and its current status. To edit a control's score, go to the page where it was originally scored.
      </div>

      {/* ── Domain tabs ── */}
      <div className="domain-tabs">
        {domains.map(d => (
          <div key={d.id} className={`domain-tab${selectedDomain===d.id?' active':''}`}
            onClick={() => setSelectedDomain(d.id)}>
            {d.id} — {d.name}
            {d.unconfirmed > 0 && (
              <span style={{marginLeft:5,fontSize:9,background:'#FFA000',color:'#fff',borderRadius:8,padding:'1px 5px'}}>{d.unconfirmed}</span>
            )}
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{display:'flex',gap:12,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <span style={{fontSize:10,color:'#7A8899',marginRight:4}}>Status:</span>
          {['all','provided','partial','missing','pending'].map(s => (
            <div key={s} className={`domain-tab${filterStatus===s?' active':''}`} style={{fontSize:10,padding:'3px 8px'}}
              onClick={() => setFilterStatus(s)}>
              {s === 'all' ? 'All' : s === 'provided' ? 'Compliant' : s === 'partial' ? 'Partial' : s === 'missing' ? 'Gap' : 'Pending'}
            </div>
          ))}
        </div>
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <span style={{fontSize:10,color:'#7A8899',marginRight:4}}>Layer:</span>
          {[
            {k:'all',l:'All'},
            {k:'auto',l:'Auto-scored'},
            {k:'upload',l:'Document'},
            {k:'manual',l:'Manual/Interview'},
          ].map(f => (
            <div key={f.k} className={`domain-tab${filterLayer===f.k?' active':''}`} style={{fontSize:10,padding:'3px 8px'}}
              onClick={() => setFilterLayer(f.k)}>
              {f.l}
            </div>
          ))}
        </div>
        <span style={{fontSize:10,color:'#B0BAC5',marginLeft:'auto'}}>{filtered.length} controls shown</span>
      </div>

      {/* ── Controls table ── */}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{width:75}}>Control</th>
              <th style={{width:120}}>Category</th>
              <th>Statement & assessor notes</th>
              <th style={{width:80}}>Source</th>
              <th style={{width:75}}>Scored via</th>
              <th style={{width:70}}>Status</th>
              <th style={{width:50}}>Score</th>
              <th style={{width:80}}>Provenance</th>
              <th style={{width:80}}>Action</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              let lastCat = '';
              return filtered.map(c => {
              const via = scoredVia(c);
              const hasScore = c.score != null;
              const needsAction = !hasScore || (c.auto_scored && !c.score_confirmed);
              const showCat = c.category !== lastCat;
              lastCat = c.category;

              return (
                <React.Fragment key={c.id}>
                {showCat && (
                  <tr>
                    <td colSpan={8} style={{padding:'10px 12px 6px',background:'#F8FAFC',borderBottom:'1px solid #EEF0F3'}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>{c.category}</div>
                      {c.objective && <div style={{fontSize:10,color:'#7A8899',marginTop:2,lineHeight:1.4,fontStyle:'italic'}}>{c.objective}</div>}
                    </td>
                  </tr>
                )}
                <tr style={{background: needsAction ? '#FFFDE7' : 'inherit'}}>
                  <td>
                    <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
                    {(c.sub_controls||[]).length > 0 && <div style={{fontSize:8,color:'#0078D4',fontWeight:500}}>+{c.sub_controls.length} sub</div>}
                    <br/><span style={{fontSize:9,color:'#B0BAC5'}}>{c.domain}</span>
                  </td>
                  <td style={{fontSize:10,color:'#546E7A'}}>{c.category}</td>
                  <td>
                    <div style={{fontSize:11,color:'#455A64',marginBottom:c.assessor_notes?4:0}}>{c.statement}</div>
                    {c.assessor_notes && (
                      <div style={{fontSize:10,color:'#7A8899',fontStyle:'italic',paddingLeft:8,borderLeft:'2px solid #E0E4E8'}}>
                        {c.assessor_notes}
                      </div>
                    )}
                    {c.score_source === 'assessor_override' && c.override_reason && (
                      <div style={{fontSize:10,color:'#E65100',fontStyle:'italic',paddingLeft:8,borderLeft:'2px solid #FFB74D',marginTop:3}}>
                        Override: {c.override_reason}
                      </div>
                    )}
                  </td>
                  <td>{sourceChip(c.source)}</td>
                  <td>
                    <span style={{fontSize:9,padding:'2px 6px',borderRadius:8,background:via.bg,color:via.color,fontWeight:500}}>
                      {via.label}
                    </span>
                  </td>
                  <td>{statusPill(c.status)}</td>
                  <td style={{fontWeight:600,color:hasScore?scoreColor(c.score):'#B0BAC5',textAlign:'center'}}>
                    {hasScore ? `${c.score}/5` : '—'}
                    {c.score_source === 'assessor_override' && c.original_auto_score && (
                      <div style={{fontSize:9,color:'#B0BAC5',textDecoration:'line-through'}}>{c.original_auto_score}/5</div>
                    )}
                  </td>
                  <td><ProvenanceBadge ctrl={c}/></td>
                  <td>
                    {!hasScore ? (
                      <button className="btn-ghost" style={{fontSize:10,padding:'3px 7px'}}
                        onClick={() => setPage(via.page)}>
                        Go to score
                      </button>
                    ) : c.auto_scored && !c.score_confirmed ? (
                      <button className="btn-ghost" style={{fontSize:10,padding:'3px 7px'}}
                        onClick={() => setPage('auto')}>
                        Review
                      </button>
                    ) : (
                      <span style={{fontSize:9,color:'#B0BAC5'}}>Complete</span>
                    )}
                  </td>
                </tr>
                {(c.sub_controls||[]).map(sc => (
                  <tr key={sc.id} style={{background:'#FAFBFD'}}>
                    <td style={{paddingLeft:20}}>
                      <span style={{fontSize:10,fontWeight:600,color:'#0078D4'}}>{sc.id}</span>
                      <br/><span style={{fontSize:8,color:'#0078D4'}}>sub of {c.id}</span>
                    </td>
                    <td style={{fontSize:10,color:'#7A8899'}}>{c.category}</td>
                    <td>
                      <div style={{fontSize:10,color:'#455A64'}}>{sc.statement}</div>
                      <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>Evidence: {sc.evidence_required}</div>
                    </td>
                    <td colSpan={5} style={{fontSize:9,color:'#7A8899',fontStyle:'italic'}}>Scored with parent</td>
                  </tr>
                ))}
                </React.Fragment>
              );
            });
            })()}
          </tbody>
        </table>
      </div>

      {/* ── Progress summary ── */}
      <div style={{marginTop:16,display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
        <span style={{fontSize:11,color:'#7A8899'}}>
          {selectedDomain}: {scored.length}/{allControls.length} scored, {confirmed.length} confirmed, {gaps.length} gaps
        </span>
        {autoUnconfirmed.length > 0 && (
          <button className="btn-ghost" style={{fontSize:11}} onClick={() => setPage('auto')}>
            Review {autoUnconfirmed.length} unconfirmed auto-scored controls
          </button>
        )}
        {pending.length > 0 && (
          <button className="btn-ghost" style={{fontSize:11}} onClick={() => setPage('manual')}>
            Score {pending.length} pending controls
          </button>
        )}
      </div>
    </div>
  );
}
